import React, { useState, useEffect } from "react";
import { getFirestore, collection, addDoc, getDocs } from "firebase/firestore";

const CreateLaboratoryWithRooms = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const db = getFirestore();

  const [laboratoryName, setLaboratoryName] = useState("");
  const [structures, setStructures] = useState([]);
  const [selectedStructure, setSelectedStructure] = useState("");
  const [newStructure, setNewStructure] = useState("");
  const [isCreatingNewStructure, setIsCreatingNewStructure] = useState(false);
  const [comments, setComments] = useState([
    "2 ordos",
    "Ordo renouvelable",
    "Mutuelle OK",
    "Mutuelle par mail",
    "CMU",
    "Urines OK",
  ]);
  const [currentComment, setCurrentComment] = useState("");
  const [numberOfRooms, setNumberOfRooms] = useState(6);

  useEffect(() => {
    const fetchStructures = async () => {
      const laboratoriesRef = collection(db, "laboratories");
      const querySnapshot = await getDocs(laboratoriesRef);
      const fetchedStructures = new Set();
      querySnapshot.forEach((doc) => {
        if (doc.data().structure) {
          fetchedStructures.add(doc.data().structure);
        }
      });
      setStructures(Array.from(fetchedStructures));
    };

    fetchStructures();
  }, [db]);

  const handleAddComment = () => {
    if (currentComment.trim() !== "") {
      setComments([...comments, currentComment.trim()]);
      setCurrentComment("");
    }
  };

  const handleRemoveComment = (index) => {
    setComments(comments.filter((_, i) => i !== index));
  };

  const handleStructureChange = (e) => {
    const value = e.target.value;
    if (value === "__CREATE_NEW__") {
      setIsCreatingNewStructure(true);
      setSelectedStructure("__CREATE_NEW__");
    } else {
      setIsCreatingNewStructure(false);
      setSelectedStructure(value);
    }
  };

  const createLaboratory = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    const structureToSave = isCreatingNewStructure
      ? newStructure
      : selectedStructure;

    if (!laboratoryName || !structureToSave || numberOfRooms <= 0) {
      setMessage("Veuillez remplir tous les champs obligatoires.");
      setLoading(false);
      return;
    }

    try {
      const labRef = await addDoc(collection(db, "laboratories"), {
        name: laboratoryName,
        structure: structureToSave,
      });

      const labId = labRef.id;

      for (const comment of comments) {
        await addDoc(collection(db, `laboratories/${labId}/comments`), {
          text: comment,
        });
      }

      for (let i = 1; i <= numberOfRooms; i++) {
        await addDoc(collection(db, "rooms"), {
          name: `Salle ${i}`,
          laboratoryId: labId,
          appointementId: "",
          locked: false,
          start_locker: null,
        });
      }

      setMessage("Laboratoire, commentaires et salles créés avec succès !");
      setLaboratoryName("");
      setSelectedStructure("");
      setNewStructure("");
      setIsCreatingNewStructure(false);
      setComments([]);
      setNumberOfRooms(1);
    } catch (error) {
      console.error("Erreur lors de la création :", error);
      setMessage("Une erreur est survenue lors de la création.");
    }

    setLoading(false);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Créer un laboratoire et ses salles</h2>
      <form onSubmit={createLaboratory} style={styles.form}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Nom du laboratoire :</label>
          <input
            type="text"
            value={laboratoryName}
            onChange={(e) => setLaboratoryName(e.target.value)}
            required
            style={styles.input}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Structure du laboratoire (SEL) :</label>
          <select
            value={selectedStructure}
            onChange={handleStructureChange}
            required={!isCreatingNewStructure}
            style={styles.select}
          >
            <option value="">Sélectionnez une structure</option>
            {structures.map((structure) => (
              <option key={structure} value={structure}>
                {structure}
              </option>
            ))}
            <option value="__CREATE_NEW__">
              -- Créer une nouvelle structure --
            </option>
          </select>
        </div>

        {isCreatingNewStructure && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Nom de la nouvelle structure :</label>
            <input
              type="text"
              value={newStructure}
              onChange={(e) => setNewStructure(e.target.value)}
              required
              style={styles.input}
            />
          </div>
        )}

        <div style={styles.formGroup}>
          <label style={styles.label}>Commentaires :</label>
          <div style={styles.commentInputContainer}>
            <input
              type="text"
              value={currentComment}
              onChange={(e) => setCurrentComment(e.target.value)}
              style={styles.input}
              placeholder="Ajouter un commentaire..."
            />
            <button
              type="button"
              onClick={handleAddComment}
              style={styles.addButton}
            >
              Ajouter
            </button>
          </div>
          <ul style={styles.commentList}>
            {comments.map((comment, index) => (
              <li key={index} style={styles.commentListItem}>
                <span>{comment}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveComment(index)}
                  style={styles.removeButton}
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Nombre de salles :</label>
          <input
            type="number"
            value={numberOfRooms}
            onChange={(e) =>
              setNumberOfRooms(Math.max(1, parseInt(e.target.value, 10) || 1))
            }
            min="1"
            required
            style={styles.input}
          />
        </div>

        <button type="submit" disabled={loading} style={styles.submitButton}>
          {loading ? "Création en cours..." : "Créer le laboratoire"}
        </button>
      </form>
      {message && (
        <p
          style={{
            ...styles.message,
            backgroundColor: message.includes("erreur")
              ? "rgba(255, 0, 0, 0.1)"
              : "rgba(0, 255, 0, 0.1)",
            borderColor: message.includes("erreur")
              ? "rgba(255, 0, 0, 0.5)"
              : "rgba(0, 255, 0, 0.5)",
          }}
        >
          {message}
        </p>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: "40px",
    maxWidth: "700px",
    margin: "40px auto",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    fontFamily: "Arial, sans-serif",
  },
  title: {
    textAlign: "center",
    color: "#333",
    marginBottom: "30px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
  },
  formGroup: {
    marginBottom: "20px",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "bold",
    color: "#555",
  },
  input: {
    width: "100%",
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxSizing: "border-box",
    fontSize: "16px",
  },
  select: {
    width: "100%",
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    backgroundColor: "white",
    fontSize: "16px",
  },
  commentInputContainer: {
    display: "flex",
    gap: "10px",
  },
  addButton: {
    padding: "12px 20px",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
    transition: "background-color 0.3s",
  },
  commentList: {
    listStyleType: "none",
    padding: 0,
    marginTop: "10px",
  },
  commentListItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px",
    backgroundColor: "#fff",
    border: "1px solid #eee",
    borderRadius: "4px",
    marginTop: "5px",
  },
  removeButton: {
    background: "transparent",
    color: "#ff4d4d",
    border: "none",
    cursor: "pointer",
    fontSize: "20px",
    lineHeight: "1",
  },
  submitButton: {
    padding: "15px",
    width: "100%",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "18px",
    fontWeight: "bold",
  },
  message: {
    marginTop: "20px",
    padding: "15px",
    borderRadius: "4px",
    border: "1px solid",
    textAlign: "center",
  },
};

export default CreateLaboratoryWithRooms;
